import type { SupabaseClient } from '@supabase/supabase-js';
import type { DealTier, GroundedDeal } from '../../services/geminiService';
import { resolveMarketDeals, haversineMiles } from './marketDiscovery';

/**
 * Shared "Basket IQ" ranking engine, used by both the Basket IQ page
 * (components/ComparisonView.tsx) and the CartAssistant optimize_basket
 * tool (src/lib/cartAssistant/registry.ts) — previously two hand-duplicated implementations.
 */

// ---- Types ---------------------------------------------------------------

export interface StorePricePoint {
  price: number;
  tier: DealTier;
  // Carried through even though nothing consumes them yet — specifically so
  // the planned distance/travel-time-aware routing feature (PERSONAL_NOTES.md)
  // can extend computeStoreTotalsAndSplit later without another data-layer pass.
  lat?: number | null;
  lng?: number | null;
  distanceMiles?: number | null;
}

// itemKey -> storeName -> best known price for that store
export type StorePriceMap = Record<string, Record<string, StorePricePoint>>;

export interface MergeSourcesInput {
  itemKey: string;
  // Raw per-store history arrays (unsorted) — this function resolves "latest".
  historyStores?: Record<string, Array<{ price: number; date: string }>> | null;
  // Rows for this item from getBatchGlobalLatticePrices.
  latticeBatchRows?: Array<{
    store_name: string;
    price: number;
    latitude?: number | null;
    longitude?: number | null;
    distance_miles?: number | null;
  }> | null;
  // Cached or freshly-resolved Tier 1 + Tier 2/3 blend for this item.
  groundedDeals?: GroundedDeal[] | null;
}

export interface BasketOptimizationItem {
  itemKey: string;
  displayName: string;
  quantity: number;
  // Price at the item's "home"/current store — used as the Smart Split
  // fallback when no cheaper option is found among the top route stores.
  currentPrice?: number | null;
}

export interface StoreItemBreakdownEntry {
  itemKey: string;
  displayName: string;
  price: number;
  tier: DealTier;
  subtotal: number;
  isBestPrice: boolean;
  lat?: number | null;
  lng?: number | null;
  distanceMiles?: number | null;
}

export interface StoreTotalRank {
  storeName: string;
  total: number;
  matches: number;
  coverage: number; // matches / basketItemCount * 100
  itemBreakdown: StoreItemBreakdownEntry[];
}

export interface SmartSplitPlanEntry {
  itemKey: string;
  displayName: string;
  store: string;
  price: number;
  tier: DealTier;
  subtotal: number;
  lat?: number | null;
  lng?: number | null;
  distanceMiles?: number | null;
}

export interface SmartSplitPlan {
  total: number;
  // Items routed to a store other than currentStoreName.
  matches: number;
  plan: SmartSplitPlanEntry[];
  // Haversine estimate of one round trip (home -> every plan store -> home,
  // see estimateRoundTripCostDollars) — only present when an origin was
  // supplied. enrichSmartSplitWithRoute's real Mapbox figure supersedes it
  // for display once that resolves.
  tripCostDollars?: number;
}

// ---- Pure merge ------------------------------------------------------------

/**
 * Collision rule (the "Hierarchy of Truth" resolveMarketDeals already applies
 * within one call, reapplied here across ALL sources): a 'verified' price at a
 * store always beats an 'estimate' at the same store. Within the same tier,
 * sources are processed history -> latticeBatchRows -> groundedDeals, and a
 * later source unconditionally overwrites an earlier one — this mirrors
 * ComparisonView's existing "assume global lattice is freshest" behavior
 * rather than a lowest-price tie-break.
 */
export function mergeStorePriceSources(inputs: MergeSourcesInput[]): StorePriceMap {
  const map: StorePriceMap = {};

  const setPoint = (bucket: Record<string, StorePricePoint>, storeName: string, point: StorePricePoint) => {
    if (!storeName) return;
    const existing = bucket[storeName];
    const shouldOverwrite =
      !existing ||
      (point.tier === 'verified' && existing.tier === 'estimate') ||
      point.tier === existing.tier;
    if (shouldOverwrite) {
      bucket[storeName] = point;
    }
  };

  for (const input of inputs) {
    if (!map[input.itemKey]) map[input.itemKey] = {};
    const bucket = map[input.itemKey];

    if (input.historyStores) {
      Object.entries(input.historyStores).forEach(([storeName, priceHistory]) => {
        if (!priceHistory || priceHistory.length === 0) return;
        const latest = [...priceHistory].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        )[0];
        setPoint(bucket, storeName, { price: latest.price, tier: 'verified' });
      });
    }

    if (input.latticeBatchRows) {
      input.latticeBatchRows.forEach((row) => {
        if (row.price == null) return;
        setPoint(bucket, row.store_name, {
          price: Number(row.price),
          tier: 'verified',
          lat: row.latitude ?? null,
          lng: row.longitude ?? null,
          distanceMiles: row.distance_miles != null ? Number(row.distance_miles) : null,
        });
      });
    }

    if (input.groundedDeals) {
      input.groundedDeals.forEach((deal) => {
        setPoint(bucket, deal.storeName, {
          price: deal.price,
          tier: deal.tier,
          distanceMiles: deal.distance ?? null,
          lat: deal.lat ?? null,
          lng: deal.lng ?? null,
        });
      });
    }
  }

  return map;
}

// ---- Route cost model (PERSONAL_NOTES.md "Basket IQ: Distance & Travel-Time-
// Aware Route Optimization" — the "Route cost model" bullet) ---------------
//
// Converts a detour to a store into a dollar figure so it can be weighed
// directly against the item-price savings that store offers, per the formula:
//
//   trip_cost = (detour_miles x IRS_STANDARD_MILEAGE_RATE_PER_MILE)
//             + (detour_hours x value_of_time_per_hour)
//
// A store is only added to the route when its item savings exceed this cost.
//
// IRS_STANDARD_MILEAGE_RATE_PER_MILE is the same rate the IRS publishes for
// valuing business vehicle use — chosen specifically because it's an
// independently verifiable, publicly-cited figure rather than an invented
// assumption. The IRS revises it (normally annually, but rates can and have
// changed mid-year — e.g. a 2026-07-01 adjustment) so this must be reviewed
// periodically, not treated as a permanent constant.
// Source: https://www.irs.gov/newsroom/irs-sets-2026-business-standard-mileage-rate-at-725-cents-per-mile-up-25-cents
// Source: https://www.littler.com/news-analysis/asap/irs-increases-standard-mileage-rate-second-half-2026-76-cents-mile
export const IRS_STANDARD_MILEAGE_RATE_PER_MILE = 0.76; // effective 2026-07-01

// Used only when the user hasn't set their own value in Intelligence
// Calibration settings (settings.intelligence.value_of_time_per_hour).
export const DEFAULT_VALUE_OF_TIME_PER_HOUR = 20;

// v1 approximation: computeStoreTotalsAndSplit must stay synchronous (no
// network call during scoring — see the "Architecture note" on Basket IQ in
// PERSONAL_NOTES.md), so detour time here is estimated from Haversine
// distance at an assumed average local-shopping-trip speed, not fetched from
// a real routing API. enrichSmartSplitWithRoute() (below) still fetches real
// Mapbox distance/time for what's actually displayed once it resolves.
//
// Calibrated 2026-10-14 against real Mapbox round trips in Fresno/Clovis
// (1 to 4 stops): road distance ran ~1.3x the straight line, at ~23 mph
// average. The previous 30 mph with no road factor came out 30-60% low.
// Long legs are mostly highway: a single 23 mph speed priced a Fresno to
// San Francisco round trip at $681 against Mapbox's real $451, so each leg's
// first LOCAL_LEG_MILES are timed at local speed and the rest at highway speed.
export const ASSUMED_LOCAL_DRIVING_SPEED_MPH = 23;
export const ASSUMED_HIGHWAY_SPEED_MPH = 55;
export const LOCAL_LEG_MILES = 10;
export const ROAD_DISTANCE_FACTOR = 1.3;

// Hours to drive one leg of `miles` road miles.
function legHours(miles: number, localSpeedMph: number): number {
  const local = Math.min(miles, LOCAL_LEG_MILES);
  return local / localSpeedMph + (miles - local) / ASSUMED_HIGHWAY_SPEED_MPH;
}

/**
 * Round-trip detour cost (there and back) from origin to a candidate store,
 * in dollars — distance component from the IRS mileage rate, time component
 * from the user's self-assessed value of time. See the module-level comment
 * above for the full formula and its sourcing.
 */
export function estimateDetourCostDollars(
  origin: { lat: number; lng: number },
  store: { lat: number; lng: number },
  opts?: { mileageRate?: number; valueOfTimePerHour?: number; speedMph?: number }
): { detourMiles: number; costDollars: number } {
  const mileageRate = opts?.mileageRate ?? IRS_STANDARD_MILEAGE_RATE_PER_MILE;
  const valueOfTimePerHour = opts?.valueOfTimePerHour ?? DEFAULT_VALUE_OF_TIME_PER_HOUR;
  const speedMph = opts?.speedMph ?? ASSUMED_LOCAL_DRIVING_SPEED_MPH;

  const oneWayMiles = haversineMiles(origin.lat, origin.lng, store.lat, store.lng) * ROAD_DISTANCE_FACTOR;
  const detourMiles = oneWayMiles * 2;
  const detourHours = legHours(oneWayMiles, speedMph) * 2;

  const costDollars = detourMiles * mileageRate + detourHours * valueOfTimePerHour;
  return { detourMiles, costDollars };
}

/**
 * Estimated cost of one trip from origin through every stop and back home,
 * visiting stops nearest-first (Haversine x ROAD_DISTANCE_FACTOR). Used for display until the real
 * Mapbox route resolves, or when it can't (no Mapbox key, network failure).
 * Unlike summing estimateDetourCostDollars per store, this doesn't charge a
 * separate there-and-back drive for each store.
 */
export function estimateRoundTripCostDollars(
  origin: { lat: number; lng: number },
  stops: Array<{ lat: number; lng: number }>,
  opts?: { mileageRate?: number; valueOfTimePerHour?: number; speedMph?: number }
): { miles: number; costDollars: number } {
  const mileageRate = opts?.mileageRate ?? IRS_STANDARD_MILEAGE_RATE_PER_MILE;
  const valueOfTimePerHour = opts?.valueOfTimePerHour ?? DEFAULT_VALUE_OF_TIME_PER_HOUR;
  const speedMph = opts?.speedMph ?? ASSUMED_LOCAL_DRIVING_SPEED_MPH;

  const remaining = [...stops];
  let current = origin;
  let miles = 0;
  let hours = 0;
  const addLeg = (straightMiles: number) => {
    const road = straightMiles * ROAD_DISTANCE_FACTOR;
    miles += road;
    hours += legHours(road, speedMph);
  };
  while (remaining.length > 0) {
    let nearestIdx = 0;
    let nearestMiles = Infinity;
    remaining.forEach((stop, idx) => {
      const d = haversineMiles(current.lat, current.lng, stop.lat, stop.lng);
      if (d < nearestMiles) {
        nearestMiles = d;
        nearestIdx = idx;
      }
    });
    addLeg(nearestMiles);
    current = remaining.splice(nearestIdx, 1)[0];
  }
  if (stops.length > 0) addLeg(haversineMiles(current.lat, current.lng, origin.lat, origin.lng));

  return { miles, costDollars: miles * mileageRate + hours * valueOfTimePerHour };
}

const hasCoords = (p: { lat?: number | null; lng?: number | null } | null | undefined): p is { lat: number; lng: number } =>
  !!p && typeof p.lat === 'number' && typeof p.lng === 'number';

// One location per store, from whichever of its prices (any item) carries
// coordinates. A store name is already branch-specific ("Name, Street"), and
// a price without coordinates shouldn't leave its store off the map when
// another item's price at the same store has them.
function buildStoreCoords(items: BasketOptimizationItem[], priceMap: StorePriceMap): Map<string, { lat: number; lng: number }> {
  const coords = new Map<string, { lat: number; lng: number }>();
  items.forEach((item) => {
    Object.entries(priceMap[item.itemKey] || {}).forEach(([storeName, point]) => {
      if (!coords.has(storeName) && hasCoords(point)) coords.set(storeName, { lat: point.lat, lng: point.lng });
    });
  });
  return coords;
}

// ---- Option comparison -----------------------------------------------------

/**
 * Another option for the same product that is at least as cheap AND closer,
 * so this one can't win on either count (e.g. the same price 150 miles
 * farther away). Returns the best such option, or null when this option is a
 * real trade-off (cheaper but farther, or closer but pricier) or when
 * distances are unknown. Callers grey the option out and show why, rather
 * than hiding it: the user may still have a reason to buy there.
 */
export function findDominatingOption<T extends { storeName: string; price: number; distance?: number | null }>(
  option: T,
  options: T[]
): T | null {
  if (option.distance == null) return null;
  const dominators = options.filter((other) =>
    other !== option &&
    other.storeName !== option.storeName &&
    other.distance != null &&
    other.price <= option.price &&
    // Distances display to 0.1 mi; anything closer than that would read
    // as "closer" next to an identical-looking distance.
    other.distance <= option.distance - 0.1
  );
  if (dominators.length === 0) return null;
  return dominators.sort((a, b) => a.price - b.price || (a.distance as number) - (b.distance as number))[0];
}

// ---- Pure ranking algorithm -------------------------------------------------

export const DEFAULT_MAX_ROUTE_STORES = 3;

export function computeStoreTotalsAndSplit(
  items: BasketOptimizationItem[],
  priceMap: StorePriceMap,
  currentStoreName: string,
  opts?: {
    maxRouteStores?: number;
    // When provided, extra stores beyond the base store are only added to
    // the route if their item savings beat the detour cost to reach them
    // (see the route cost model above). Without an origin there's nothing to
    // measure a detour against, so this gating is skipped entirely and
    // selection falls back to the pre-existing coverage/price-only ranking.
    origin?: { lat: number; lng: number } | null;
    valueOfTimePerHour?: number;
    mileageRate?: number;
  }
): { storeTotals: StoreTotalRank[]; smartSplit: SmartSplitPlan | null } {
  if (items.length === 0) return { storeTotals: [], smartSplit: null };
  const maxRouteStores = opts?.maxRouteStores ?? DEFAULT_MAX_ROUTE_STORES;

  const storeCoords = buildStoreCoords(items, priceMap);
  // Straight-line miles from the origin; stores we can't place sort last.
  const distanceFromOrigin = (storeName: string): number => {
    const c = storeCoords.get(storeName);
    return opts?.origin && c ? haversineMiles(opts.origin.lat, opts.origin.lng, c.lat, c.lng) : Infinity;
  };
  const allStores = new Set<string>();
  items.forEach((item) => {
    const prices = priceMap[item.itemKey];
    if (prices) Object.keys(prices).forEach((s) => allStores.add(s));
  });

  const storeTotals: StoreTotalRank[] = Array.from(allStores)
    .map((storeName) => {
      let total = 0;
      let matches = 0;
      const itemBreakdown: StoreItemBreakdownEntry[] = [];

      items.forEach((item) => {
        const point = priceMap[item.itemKey]?.[storeName];
        if (point) {
          const subtotal = point.price * item.quantity;
          total += subtotal;
          matches++;
          itemBreakdown.push({
            itemKey: item.itemKey,
            displayName: item.displayName,
            price: point.price,
            tier: point.tier,
            subtotal,
            isBestPrice: false,
            lat: storeCoords.get(storeName)?.lat ?? null,
            lng: storeCoords.get(storeName)?.lng ?? null,
            distanceMiles: point.distanceMiles,
          });
        }
      });

      return { storeName, total, matches, coverage: (matches / items.length) * 100, itemBreakdown };
    })
    .filter((r) => r.matches > 0)
    .sort((a, b) => b.matches - a.matches || a.total - b.total || distanceFromOrigin(a.storeName) - distanceFromOrigin(b.storeName));

  // Mark the globally-cheapest store(s) per item across the whole comparison.
  const globalBestByItem: Record<string, number> = {};
  items.forEach((item) => {
    let min = item.currentPrice ?? Infinity;
    storeTotals.forEach((store) => {
      const entry = store.itemBreakdown.find((ib) => ib.itemKey === item.itemKey);
      if (entry && entry.price < min) min = entry.price;
    });
    globalBestByItem[item.itemKey] = min;
  });
  storeTotals.forEach((store) => {
    store.itemBreakdown.forEach((ib) => {
      if (ib.price <= globalBestByItem[ib.itemKey]) ib.isBestPrice = true;
    });
  });

  // Smart Split store selection. Two modes:
  //
  // Without an origin (legacy/no-location behavior): take the top N stores
  // by coverage/price, unchanged from before.
  //
  // With an origin: the base store (best coverage/price) always anchors the
  // route with zero detour cost — you're going somewhere regardless. Every
  // other store is a candidate EXTRA stop, accepted only if the item savings
  // it uniquely contributes beat its round-trip detour cost (the "Route cost
  // model" from PERSONAL_NOTES.md). This is a greedy heuristic evaluated in
  // the existing coverage/price rank order, not a globally optimal solve.
  const origin = opts?.origin;
  const acceptedStoreNames = new Set<string>();

  if (origin) {
    // The base store is the one trip you make regardless, so pick it on what
    // it really costs: among the stores carrying the most items, the lowest
    // item total plus the drive there and back. Ranking on price alone sent
    // people to a farther store over an equal (or near-equal) price close by.
    // Stores without coordinates can't be costed, so they only win when no
    // store with the same coverage can be placed.
    const maxMatches = storeTotals[0]?.matches ?? 0;
    const baseCost = (store: StoreTotalRank): number => {
      const coords = storeCoords.get(store.storeName);
      if (!coords) return Infinity;
      return store.total + estimateDetourCostDollars(origin, coords, {
        mileageRate: opts?.mileageRate,
        valueOfTimePerHour: opts?.valueOfTimePerHour,
      }).costDollars;
    };
    const baseStore = storeTotals
      .filter((s) => s.matches === maxMatches)
      .reduce<StoreTotalRank | undefined>((best, s) => (!best || baseCost(s) < baseCost(best) ? s : best), undefined);
    if (baseStore) acceptedStoreNames.add(baseStore.storeName);

    // Best price found so far per item, seeded from the base store (or the
    // existing currentPrice fallback) — later candidates are only "worth it"
    // for the savings they add beyond what's already covered, so the same
    // item's price never gets counted as savings twice.
    const bestPriceSoFar: Record<string, number> = {};
    items.forEach((item) => {
      const basePoint = baseStore ? priceMap[item.itemKey]?.[baseStore.storeName] : undefined;
      bestPriceSoFar[item.itemKey] = basePoint?.price ?? item.currentPrice ?? Infinity;
    });

    storeTotals
      .filter((s) => s.storeName !== baseStore?.storeName)
      .forEach((candidate) => {
        if (acceptedStoreNames.size >= maxRouteStores) return;

        const itemSavings = candidate.itemBreakdown.reduce((sum, ib) => {
          const item = items.find((i) => i.itemKey === ib.itemKey);
          if (!item) return sum;
          const currentBest = bestPriceSoFar[ib.itemKey] ?? Infinity;
          return ib.price < currentBest ? sum + (currentBest - ib.price) * item.quantity : sum;
        }, 0);
        if (itemSavings <= 0) return;

        const coords = storeCoords.get(candidate.storeName) ?? null;
        // No coordinates to measure a detour against — fall back to the
        // pre-existing "cheaper wins" behavior for this one store rather
        // than silently excluding it for a data gap, not a real cost reason.
        if (!coords) {
          acceptedStoreNames.add(candidate.storeName);
          candidate.itemBreakdown.forEach((ib) => {
            if (ib.price < (bestPriceSoFar[ib.itemKey] ?? Infinity)) bestPriceSoFar[ib.itemKey] = ib.price;
          });
          return;
        }

        const { costDollars } = estimateDetourCostDollars(origin, coords, {
          mileageRate: opts?.mileageRate,
          valueOfTimePerHour: opts?.valueOfTimePerHour,
        });
        if (itemSavings > costDollars) {
          acceptedStoreNames.add(candidate.storeName);
          candidate.itemBreakdown.forEach((ib) => {
            if (ib.price < (bestPriceSoFar[ib.itemKey] ?? Infinity)) bestPriceSoFar[ib.itemKey] = ib.price;
          });
        }
      });
  } else {
    storeTotals.slice(0, maxRouteStores).forEach((s) => acceptedStoreNames.add(s.storeName));
  }

  let splitTotal = 0;
  let splitMatches = 0;
  const plan: SmartSplitPlanEntry[] = [];

  items.forEach((item) => {
    let bestPrice = item.currentPrice ?? Infinity;
    let bestStore = currentStoreName;
    let bestPoint: StorePricePoint | null = null;

    Object.entries(priceMap[item.itemKey] || {}).forEach(([storeName, point]) => {
      if (acceptedStoreNames.has(storeName) && point.price < bestPrice) {
        bestPrice = point.price;
        bestStore = storeName;
        bestPoint = point;
      }
    });

    // No accepted store carries this item at all — every basket item must
    // still be accounted for, so fall back to its own cheapest price
    // anywhere, accepting that store as a necessary extra stop regardless of
    // detour cost (there's no alternative way to get this item).
    if (bestPrice === Infinity) {
      Object.entries(priceMap[item.itemKey] || {}).forEach(([storeName, point]) => {
        if (point.price < bestPrice) {
          bestPrice = point.price;
          bestStore = storeName;
          bestPoint = point;
        }
      });
      if (bestPrice !== Infinity && bestStore !== currentStoreName && !acceptedStoreNames.has(bestStore)) {
        acceptedStoreNames.add(bestStore);
      }
    }

    if (bestPrice !== Infinity) {
      const subtotal = bestPrice * item.quantity;
      splitTotal += subtotal;
      if (bestStore !== currentStoreName) splitMatches++;
      plan.push({
        itemKey: item.itemKey,
        displayName: item.displayName,
        store: bestStore,
        price: bestPrice,
        tier: bestPoint?.tier ?? 'verified',
        subtotal,
        lat: storeCoords.get(bestStore)?.lat ?? null,
        lng: storeCoords.get(bestStore)?.lng ?? null,
        distanceMiles: bestPoint?.distanceMiles,
      });
    }
  });

  // Display estimate for the whole trip (home -> every store in the plan ->
  // home). The per-store detour costs above only decide which stores are
  // worth adding; summed, they would charge a separate round trip per store
  // and nothing at all for the base store.
  let tripCostDollars: number | undefined;
  if (origin) {
    const planStops = Array.from(new Set(plan.map((p) => p.store)))
      .map((store) => storeCoords.get(store))
      .filter(hasCoords);
    tripCostDollars = estimateRoundTripCostDollars(origin, planStops, {
      mileageRate: opts?.mileageRate,
      valueOfTimePerHour: opts?.valueOfTimePerHour,
    }).costDollars;
  }

  return {
    storeTotals,
    smartSplit: splitTotal > 0 && plan.length > 0
      ? { total: splitTotal, matches: splitMatches, plan, ...(tripCostDollars != null ? { tripCostDollars } : {}) }
      : null,
  };
}

// ---- Route enrichment (Mapbox) ---------------------------------------------

export interface SmartSplitRouteInfo {
  // Store names in Mapbox's optimized visiting order.
  orderedStops: string[];
  // The same stops with the coordinates the route was computed from.
  stops: Array<{ storeName: string; lat: number; lng: number }>;
  totalDistanceMiles: number;
  totalDurationMinutes: number;
  // GeoJSON LineString ([lng,lat] pairs) — opaque here, interpreted only by
  // components/SmartSplitRouteMap.tsx.
  geometry: { type: 'LineString'; coordinates: [number, number][] } | null;
  // Baseline: driving straight to the single cheapest store alone. Present
  // only when that baseline route also resolved successfully.
  baselineSingleStoreMiles?: number;
  baselineSingleStoreMinutes?: number;
  extraMinutesVsSingleStore?: number;
  // Real trip cost from actual Mapbox distance/time (a round trip, including
  // the drive home), using the same formula as estimateDetourCostDollars —
  // supersedes smartSplit.tripCostDollars (the Haversine estimate).
  tripCostDollars?: number;
}

interface RouteOptimizeApiResult {
  orderedStoreNames: string[];
  totalDistanceMeters: number;
  totalDurationSeconds: number;
  geometry: { type: 'LineString'; coordinates: [number, number][] } | null;
}

// Duplicated per-module private helper — same convention as
// src/lib/marketDiscovery.ts's getMarketDiscoverToken (this codebase doesn't
// share this helper across modules today).
async function getRouteEnrichmentToken(): Promise<string | null> {
  try {
    if ((window as any).Clerk?.session) {
      return await (window as any).Clerk.session.getToken({ template: 'supabase' });
    }
  } catch (e) {
    console.warn('[basketOptimization] Failed to get Clerk token:', e);
  }
  return null;
}

async function callRouteOptimizeApi(
  origin: { lat: number; lng: number },
  waypoints: Array<{ lat: number; lng: number; storeName: string }>
): Promise<RouteOptimizeApiResult | null> {
  try {
    const token = await getRouteEnrichmentToken();
    const response = await fetch('/api/route/optimize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ origin, waypoints }),
    });
    // Covers 503 (Mapbox unconfigured), 502 (Mapbox request failed), 400s alike —
    // enrichment is opt-in, so any non-2xx just means "no route info this time".
    if (!response.ok) return null;
    return await response.json();
  } catch (err) {
    console.error('[basketOptimization] Route optimize call failed:', err);
    return null;
  }
}

/**
 * Opt-in async enrichment over an ALREADY-COMPUTED synchronous smartSplit
 * result. Never mutates the price-based selection itself — layers real
 * driving distance/time/order on top of the up-to-maxRouteStores stores
 * computeStoreTotalsAndSplit already picked by price. Always resolves
 * (never throws); degrades to `{ ...smartSplit }` with no `route` field
 * when Mapbox is unconfigured, the network call fails, or no candidate
 * store has usable coordinates — callers should fall back to each plan
 * entry's own `distanceMiles` (Haversine, already available) labeled as a
 * straight-line estimate in that case.
 */
export async function enrichSmartSplitWithRoute(
  smartSplit: SmartSplitPlan,
  origin: { lat: number; lng: number },
  bestSingleStore?: StoreTotalRank | null,
  opts?: { mileageRate?: number; valueOfTimePerHour?: number }
): Promise<SmartSplitPlan & { route?: SmartSplitRouteInfo }> {
  const candidatesByStore = new Map<string, { lat: number; lng: number }>();
  smartSplit.plan.forEach((entry) => {
    if (!candidatesByStore.has(entry.store) && typeof entry.lat === 'number' && typeof entry.lng === 'number') {
      candidatesByStore.set(entry.store, { lat: entry.lat, lng: entry.lng });
    }
  });
  if (candidatesByStore.size === 0) return { ...smartSplit };

  const waypoints = Array.from(candidatesByStore.entries()).map(([storeName, coords]) => ({ storeName, ...coords }));
  const routeResult = await callRouteOptimizeApi(origin, waypoints);
  if (!routeResult) return { ...smartSplit };

  const mileageRate = opts?.mileageRate ?? IRS_STANDARD_MILEAGE_RATE_PER_MILE;
  const valueOfTimePerHour = opts?.valueOfTimePerHour ?? DEFAULT_VALUE_OF_TIME_PER_HOUR;
  const totalDistanceMiles = routeResult.totalDistanceMeters / 1609.34;
  const totalDurationMinutes = routeResult.totalDurationSeconds / 60;

  const route: SmartSplitRouteInfo = {
    orderedStops: routeResult.orderedStoreNames,
    stops: routeResult.orderedStoreNames
      .filter((storeName) => candidatesByStore.has(storeName))
      .map((storeName) => ({ storeName, ...candidatesByStore.get(storeName)! })),
    totalDistanceMiles,
    totalDurationMinutes,
    geometry: routeResult.geometry,
    tripCostDollars: totalDistanceMiles * mileageRate + (totalDurationMinutes / 60) * valueOfTimePerHour,
  };

  // Baseline single-store comparison — reuse the multi-stop result if the
  // route only has one stop anyway; otherwise fetch a dedicated 1-waypoint
  // baseline route so both drive times come from the same real-driving
  // source (not mixing Mapbox time with Haversine distance).
  const baselineCoords = bestSingleStore ? candidatesByStore.get(bestSingleStore.storeName) : undefined;
  if (bestSingleStore && candidatesByStore.size === 1 && candidatesByStore.has(bestSingleStore.storeName)) {
    route.baselineSingleStoreMiles = route.totalDistanceMiles;
    route.baselineSingleStoreMinutes = route.totalDurationMinutes;
    route.extraMinutesVsSingleStore = 0;
  } else if (baselineCoords) {
    const baselineResult = await callRouteOptimizeApi(origin, [{ storeName: bestSingleStore!.storeName, ...baselineCoords }]);
    if (baselineResult) {
      route.baselineSingleStoreMiles = baselineResult.totalDistanceMeters / 1609.34;
      route.baselineSingleStoreMinutes = baselineResult.totalDurationSeconds / 60;
      route.extraMinutesVsSingleStore = route.totalDurationMinutes - route.baselineSingleStoreMinutes;
    }
  }

  return { ...smartSplit, route };
}

// ---- Bounded-concurrency live-discovery fallback ----------------------------

// A genuinely uncovered item always hits resolveMarketDeals's Serper+Gemini
// path (MIN_LOCAL_LATTICE_DENSITY in marketDiscovery.ts guarantees this), so
// treat every uncovered item as near-worst-case latency when picking caps.
export const COMPARISON_LIVE_DISCOVERY_CAP = 6;
export const CHAT_LIVE_DISCOVERY_CAP = 3;
export const BATCH_LIVE_DISCOVERY_DELAY_MS = 1200;

export interface UncoveredItemQuery {
  itemKey: string;
  queryName: string;
  price?: number | null;
  size?: number | string | null;
  unit?: string | null;
}

export interface ResolveUncoveredItemsOptions {
  supabase: SupabaseClient;
  items: UncoveredItemQuery[];
  userLat?: number | null;
  userLng?: number | null;
  userAddress?: string | null;
  radiusMiles?: number;
  lookbackHours?: number;
  maxItems: number;
  // 0 for interactive callers (e.g. chat tools), BATCH_LIVE_DISCOVERY_DELAY_MS
  // for batch/page-level callers — mirrors services/geminiService.ts's
  // searchGrounding(item, store, batchSize, skipDelay) convention: a flat
  // sequential delay for multi-item batches, skipped for interactive call sites.
  delayMs?: number;
  // Caller owns caching (Zustand metadata write-back, or local component
  // state) — this module stays state-agnostic, same as resolveMarketDeals itself.
  onItemResolved?: (itemKey: string, deals: GroundedDeal[]) => void;
}

export async function resolveUncoveredItemsLive(
  opts: ResolveUncoveredItemsOptions
): Promise<Record<string, GroundedDeal[]>> {
  const {
    supabase,
    items,
    userLat,
    userLng,
    userAddress,
    radiusMiles,
    lookbackHours,
    maxItems,
    delayMs = 0,
    onItemResolved,
  } = opts;

  const results: Record<string, GroundedDeal[]> = {};
  const targets = items.slice(0, maxItems);

  for (let i = 0; i < targets.length; i++) {
    if (i > 0 && delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    const target = targets[i];
    try {
      const deals = await resolveMarketDeals({
        supabase,
        query: target.queryName,
        itemPrice: target.price,
        itemSize: target.size,
        itemUnit: target.unit,
        userLat,
        userLng,
        userAddress,
        radiusMiles,
        lookbackHours,
      });
      results[target.itemKey] = deals;
      onItemResolved?.(target.itemKey, deals);
    } catch (err) {
      console.error(`[basketOptimization] Live discovery failed for "${target.queryName}":`, err);
      results[target.itemKey] = [];
    }
  }

  return results;
}
